import {
  WORKSPACE_NAME_MAX_LENGTH,
  WORKSPACE_NAME_MIN_LENGTH,
  getAuth0ErrorCode,
  getEmailDomain,
  getErrorMessage,
  isFreeEmailDomain,
} from '@spotify/confidence-core-react';

import { Region } from './hooks/useCreateAccount';
import type {
  AuthConnection,
  CreateAccountRequest,
} from './hooks/useCreateAccount';

export type AccountDetailsState = {
  displayName: string;
  adminEmail?: string;
  region: Region;
  loginDomain?: string;
  authConnections: Record<AuthConnection, boolean>;
  authConnectionsCustomized?: boolean;
};

type BuildCreateAccountRequestOptions = {
  accountDetails: AccountDetailsState;
  inviteId?: string;
  marketingOptIn: boolean;
  signupAttribution: CreateAccountRequest['signupAttribution'];
};

export const NAME_MAX_LENGTH = WORKSPACE_NAME_MAX_LENGTH;
export const NAME_MIN_LENGTH = WORKSPACE_NAME_MIN_LENGTH;

export const DEFAULT_STATE: AccountDetailsState = {
  displayName: '',
  region: Region.RegionEu,
  loginDomain: '',
  authConnections: {
    googleAuthConnection: false,
    passwordAuthConnection: false,
  },
};

export const getGoogleLoginMode = (
  loginDomain?: string,
): 'invite-only' | 'domain' =>
  loginDomain === undefined ? 'invite-only' : 'domain';

export const getEnabledAuthConnections = (
  authConnections: Record<AuthConnection, boolean>,
): AuthConnection[] =>
  (Object.entries(authConnections) as [AuthConnection, boolean][])
    .filter(([, enabled]) => enabled)
    .map(([connection]) => connection);

export const buildCreateAccountRequest = ({
  accountDetails,
  inviteId,
  marketingOptIn,
  signupAttribution,
}: BuildCreateAccountRequestOptions): CreateAccountRequest => ({
  displayName: accountDetails.displayName,
  region: accountDetails.region,
  authConnections: getEnabledAuthConnections(
    accountDetails.authConnections,
  ).map(connection => ({
    [connection]: {},
  })) as CreateAccountRequest['authConnections'],
  adminEmail: accountDetails.adminEmail || undefined,
  allowedLoginEmailDomains:
    inviteId && accountDetails.authConnections.googleAuthConnection
      ? [accountDetails.loginDomain].filter((domain): domain is string =>
          Boolean(domain),
        )
      : undefined,
  marketingOptIn,
  signupAttribution,
});

const displayNameRegex = /^[a-zA-Z][a-zA-Z0-9 -]*[a-zA-Z0-9]$/;

export const isDisplayNameValid = (displayName: string) => {
  return (
    displayNameRegex.test(displayName) &&
    displayName.length >= NAME_MIN_LENGTH &&
    displayName.length <= NAME_MAX_LENGTH
  );
};

export const formatDisplayName = (displayName: string) => {
  return displayName.replace(/[^a-zA-Z0-9 -]/g, '').slice(0, NAME_MAX_LENGTH);
};

const SECOND_LEVEL_DOMAIN_LABELS = ['co', 'com', 'org', 'net', 'ac', 'gov'];

export const suggestWorkspaceName = (
  email: string | undefined,
): string | undefined => {
  const domain = getEmailDomain(email);
  if (!domain || isFreeEmailDomain(domain)) return undefined;

  const labels = domain.split('.').slice(0, -1);
  if (
    labels.length > 1 &&
    SECOND_LEVEL_DOMAIN_LABELS.includes(labels[labels.length - 1])
  ) {
    labels.pop();
  }
  const organizationLabel = labels.at(-1);
  if (!organizationLabel) return undefined;

  const suggestion = formatDisplayName(
    organizationLabel.charAt(0).toUpperCase() + organizationLabel.slice(1),
  );
  return suggestion.length >= NAME_MIN_LENGTH ? suggestion : undefined;
};

const nameLengthMessage = `The name must be between ${NAME_MIN_LENGTH} and ${NAME_MAX_LENGTH} characters.`;

const workspaceNameHelperText =
  'Shown across Confidence and to your teammates.';

export const getWorkspaceNameError = ({
  displayName,
  isModified,
  showLengthError = true,
}: {
  displayName: string;
  isModified: boolean;
  showLengthError?: boolean;
}): string | undefined => {
  if (!isModified || displayName.length === 0) {
    return undefined;
  }
  if (!/^[a-zA-Z]/.test(displayName)) {
    return 'The name should start with a letter.';
  }
  if (displayName.endsWith('-') || displayName.endsWith(' ')) {
    return 'The name cannot end with "-" or a space.';
  }
  if (showLengthError && !isDisplayNameValid(displayName)) {
    return nameLengthMessage;
  }
  return undefined;
};

export const getWorkspaceNameHelperText = (
  options: Parameters<typeof getWorkspaceNameError>[0],
): string => getWorkspaceNameError(options) ?? workspaceNameHelperText;

export const isEmailNotVerifiedError = (error: unknown): boolean => {
  return (
    getAuth0ErrorCode(error) === 'email_not_verified' ||
    /email (?:is )?not verified|verify your email/i.test(
      getErrorMessage(error, ''),
    )
  );
};

export const isAccountUnderReviewError = (error?: Error) => {
  return Boolean(error && /review/i.test(error.message));
};

export const isAuthCancellationError = (error: unknown): boolean =>
  getAuth0ErrorCode(error) === 'cancelled';

export type SignupAuthOutcome = 'success' | 'error' | 'abandoned';

export const getSignupAuthOutcome = (error: unknown): SignupAuthOutcome => {
  if (isAuthCancellationError(error)) return 'abandoned';
  if (isEmailNotVerifiedError(error)) return 'success';
  return 'error';
};

const NO_ORGANIZATION_MEMBERSHIP = 'does not belong to any organization';

export const isNoOrganizationMembershipError = (error: unknown): boolean =>
  getErrorMessage(error, '').includes(NO_ORGANIZATION_MEMBERSHIP);

export const isSilentSignupAuthError = (error: unknown): boolean =>
  isAuthCancellationError(error) ||
  isEmailNotVerifiedError(error) ||
  isNoOrganizationMembershipError(error);

export const getSignupErrorMessage = (error: Error): string => {
  if (/sign-in session expired/i.test(error.message)) {
    return error.message;
  }
  const match = error.message.match(/^\w+:\s*(.+)/);
  return match?.[1] ?? error.message;
};

const UNEXPLAINED_DENIAL = /^access[\s_-]?denied\.?$/i;

export const isAuthDenialError = (error: unknown): boolean =>
  getAuth0ErrorCode(error) === 'access_denied';

export const getSignupAuthErrorMessage = (error: unknown): string => {
  const errorCode = getAuth0ErrorCode(error);
  const errorMessage = getErrorMessage(error, '').trim();

  if (errorCode === 'timeout') {
    return 'Sign-up timed out. Try again.';
  }
  if (
    ['user_exists', 'username_exists'].includes(errorCode ?? '') ||
    /user already exists/i.test(errorMessage)
  ) {
    return 'An account already exists for this email. Log in instead.';
  }
  if (
    isAuthDenialError(error) &&
    errorMessage &&
    !UNEXPLAINED_DENIAL.test(errorMessage) &&
    !isNoOrganizationMembershipError(error)
  ) {
    return errorMessage;
  }
  return "We couldn't complete sign-up. Try again or contact support.";
};

export const getLoginAuthErrorMessage = (error: unknown): string => {
  const errorCode = getAuth0ErrorCode(error);
  if (errorCode === 'timeout') {
    return 'Login timed out. Try again.';
  }
  return "We couldn't log you in. Try again or contact support.";
};

export const getSignupReturnPath = ({
  returnTo,
  origin,
}: {
  returnTo: string | null;
  origin: string;
}): string | undefined => {
  if (!returnTo) return undefined;
  try {
    const returnUrl = new URL(returnTo, origin);

    const isSignupRoute =
      returnUrl.origin === origin &&
      (returnUrl.pathname === '/signup' ||
        returnUrl.pathname.startsWith('/signup/'));
    return isSignupRoute
      ? `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`
      : undefined;
  } catch {
    return undefined;
  }
};
